import React from 'react';

import SelectorModal from 'wdna-components/lib/modals/selectors/CustomSelectorModal';

import { RowDataChangedEvent } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageBoxWarning, messageBoxInfo, } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Edit from '../forms/companies/Edit';

import { company, CompanyDO, InstallerDO } from '../../requests';

type Props = unknown;

interface State {
  rowId?: number;
  update?: any;
  loading: boolean;
  data?: CompanyDO[];
  valueListA?: any[];
  valueListS?: any[];
  showGrid?: boolean;
  showValueSelector?: boolean;
}

export default class Companies extends React.Component <Props, State> {
  private tabId: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.tabId = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      add: true,
      del: true,
      assign: true,
      toggleItem: true,
      labels: {
        assign: 'Installers',
      }
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      valueListA: [],
      valueListS: [],

      showGrid: false,
      showValueSelector: false,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      if (this.mounted) {
        const companyArray: CompanyDO[] = await company<CompanyDO[]>('list') || [];
        this.setState({
          data: companyArray,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onAssign = (value: CompanyDO): void => {
    const rowId: number = value.id;
    this.setState((prevState: State) => ({
      ...prevState,
      rowId,
    }));
    setTimeout(() => {
      this.loadValues(rowId).catch(() => { /* pass */ });
    }, 0);
  };

  public loadValues = async (rowId: number): Promise<void> => {
    try {
      const valueListFalse: InstallerDO[] = await company<InstallerDO[]>('installers', String(rowId), { assigned: 'false' }) || [];
      const valueListTrue: InstallerDO[] = await company<InstallerDO[]>('installers', String(rowId), { assigned: 'true' }) || [];

      const valueListS: number[] = [];
      valueListTrue.forEach((item: InstallerDO) => {
        valueListS.push(item.id);
      });

      this.setState((prevState: State) => ({
        ...prevState,
        update: window.getStr(),
        valueListA: valueListFalse.concat(valueListTrue),
        valueListS,
      }));
      this.updModal('showValueSelector', true);
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
  };

  public updModal = (modalName: string = 'none', sh: boolean = false): void => {
    this.setState((prevState: State) => ({
      ...prevState,
      update: window.getStr(),
      [modalName]: sh,
    }));
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'CoE';
    const title: string = rowId ? `Company Edit: ${rowId}` : 'Company New';

    window.store.dispatch(removeTab(tabId));
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title,
      component: Edit,
      props: {
        rowId,
        tabId: this.tabId,
        report: 'COMPANIES',
        title: 'Companies',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (value: CompanyDO): void => {
    window.store.dispatch(removeTab('CoE'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: CompanyDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      company('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public handleChangeValues = (counters: any[]): void => {
    const { rowId } = this.state;

    if (rowId) {
      this.updModal('showValueSelector', false);

      const valueListS: any[] = [];
      counters.forEach((counter: any) => {
        valueListS.push(counter);
      });

      this.setState({
        valueListS,
      }, () => { this.handleSubmit(rowId); });
    }
  };

  public handleSubmit = (rowId: number): void => {
    const { valueListS } = this.state;

    messageBoxInfo('Edit', 'Do you want to accept this assignment?', () => {
      this.loadingStart();
      company('installer', String(rowId), { installerId: valueListS })
        ?.then(() => {
          messageInfo('Success', 'Installers successfully assigned', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public render(): JSX.Element {
    const { loading, data, valueListA, valueListS, showValueSelector, showGrid } = this.state;

    const columnAlias: any = {
      id: 'ID',
      code: 'Code',
      name: 'Name',
      type: 'Type',
      contact: 'Contact',
      phone: 'Phone',
      email: 'E-mail'
    };
    const columnsToHide: any = [];
    const columnStyles: any = {
      name: { textAlign: 'left' },
      type: { textAlign: 'left' },
      contact: { textAlign: 'left' },
      phone: { textAlign: 'left' },
      email: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },
    };
    const selectorAlias: any = {
      name: 'Name',
      email: 'Email',
      userId: 'ID',
    };
    const selectorStyles: any = {
      name: { textAlign: 'left' },
      email: { textAlign: 'left' },
      userId: { textAlign: 'right' },
    };
    const selectorHide: any = [
      'active',
      'userId',
    ];

    return (
      <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
        <Table
          data={data || []}
          alias={columnAlias}
          hiddenCols={columnsToHide}
          customCellStyles={columnStyles}
          buttons={this.buttons}
          onEdit={this.onEdit}
          onAdd={this.onEdit}
          onDelete={this.onDelete}
          onAssign={this.onAssign}
          loading={loading}
          rowDataChanged={this.rowDataChanged}
        />
        <SelectorModal
          alias={selectorAlias}
          items={valueListA || []}
          selectedItems={valueListS}
          title="Assign installers to companies"
          hiddenCols={selectorHide}
          customCellStyles={selectorStyles}
          show={showValueSelector}
          // eslint-disable-next-line react/jsx-no-bind
          onCancel={this.updModal.bind(this, 'showValueSelector', false)}
          onSave={this.handleChangeValues}
        />
      </div>
    );
  }
}
